import { useEffect, useState, useRef } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getDocuments, saveDocument, deleteDocument, GetDocumentsOutputType } from 'zite-endpoints-sdk';
import { uploadFile } from 'zite-file-upload-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Upload, Download, Trash2, FileText, ShieldAlert, AlertTriangle, Loader2 } from 'lucide-react';
import { format, parseISO, differenceInDays } from 'date-fns';

type Doc = GetDocumentsOutputType['myDocuments'][0];

const DOC_TYPES = ['Contract', 'Policy', 'Certificate', 'Payslip', 'Right to Work', 'Disciplinary', 'Investigation', 'General'];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : null; } catch { return null; } };

function ExpiryBadge({ date }: { date?: string }) {
  if (!date) return null;
  const days = differenceInDays(parseISO(date), new Date());
  if (days < 0) return <span className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive rounded-sm">Expired</span>;
  if (days < 30) return <span className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded-sm">Expires in {days}d</span>;
  return <span className="text-xs text-muted-foreground">{fmtDate(date)}</span>;
}

function DocRow({ doc, onDelete }: { doc: Doc; onDelete: (id: string) => void }) {
  const iconMap: Record<string, typeof FileText> = { Contract: FileText, Certificate: FileText, Policy: FileText };
  const Icon = iconMap[doc.documentType || ''] || FileText;
  return (
    <div className="flex items-center gap-3 p-4 border border-border rounded-lg bg-card hover:border-border/60">
      <Icon className="w-5 h-5 text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{doc.title}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {doc.documentType && <span className="text-xs text-muted-foreground">{doc.documentType}</span>}
          {doc.expiryDate && <><span className="text-muted-foreground text-xs">·</span><ExpiryBadge date={doc.expiryDate} /></>}
          {doc.uploadDate && <span className="text-xs text-muted-foreground">· Added {fmtDate(doc.uploadDate)}</span>}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        {doc.fileUrl && <a href={doc.fileUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="h-8 w-8 p-0"><Download className="w-4 h-4" /></Button></a>}
        <AlertDialog>
          <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Remove "{doc.title}"?</AlertDialogTitle></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => onDelete(doc.id)}>Remove</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  const { user } = useAuth();
  const [docs, setDocs] = useState<GetDocumentsOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: '', documentType: 'General', permissionLevel: 'Employee Visible', expiryDate: '', notes: '', visibility: 'All Staff', visibleDepartments: [] as string[], visibleRoles: [] as string[] });
  const fileRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const isAdmin = user?.role === 'Admin' || user?.role === 'Manager';

  const load = () => {
    if (!user) return;
    getDocuments({}).then(setDocs).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [user]);

  const handleDelete = async (id: string) => {
    try {
      await deleteDocument({ id });
      toast.success('Document removed');
      setDocs(d => d ? { ...d, myDocuments: d.myDocuments.filter(x => x.id !== id), hrDocuments: d.hrDocuments.filter(x => x.id !== id) } : d);
    } catch { toast.error('Failed to delete'); }
  };

  const handleUpload = async () => {
    if (!pendingFile || !uploadForm.title) { toast.error('Please select a file and add a title'); return; }
    setUploading(true);
    try {
      const { fileUrl } = await uploadFile({ data: pendingFile, filename: pendingFile.name });
      await saveDocument({ ...uploadForm, fileUrl });
      toast.success('Document uploaded');
      setUploadOpen(false);
      setPendingFile(null);
      load();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  if (loading) return <div className="px-4 md:px-8 py-6 space-y-3 max-w-4xl mx-auto"><Skeleton className="h-10 w-40" /><Skeleton className="h-64" /></div>;

  const myDocs = docs?.myDocuments || [];
  const hrDocs = docs?.hrDocuments || [];

  return (
    <div className="min-h-screen bg-background px-4 md:px-8 py-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-xl font-bold text-foreground">Documents</h1><p className="text-sm text-muted-foreground mt-0.5">Your employment files, certificates and signed policies</p></div>
        <Button onClick={() => setUploadOpen(true)} className="bg-primary text-primary-foreground"><Upload className="w-4 h-4 mr-2" /> Upload</Button>
      </div>

      <Tabs defaultValue="mine">
        <TabsList className="mb-4">
          <TabsTrigger value="mine">My Documents ({myDocs.length})</TabsTrigger>
          {isAdmin && <TabsTrigger value="hr"><ShieldAlert className="w-3.5 h-3.5 mr-1.5" />HR Files ({hrDocs.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="mine">
          {myDocs.length === 0
            ? <div className="text-center py-12 text-muted-foreground"><FileText className="w-8 h-8 mx-auto mb-3 opacity-40" /><p className="text-sm">No documents yet. Upload your first document above.</p></div>
            : <div className="space-y-2">{myDocs.map(d => <DocRow key={d.id} doc={d} onDelete={handleDelete} />)}</div>
          }
        </TabsContent>

        {isAdmin && (
          <TabsContent value="hr">
            <div className="flex items-center gap-2 bg-destructive/5 border border-destructive/20 rounded-lg px-4 py-2 mb-4 text-xs text-destructive">
              <AlertTriangle className="w-4 h-4 shrink-0" /> Admin only — these documents are not visible to employees
            </div>
            {hrDocs.length === 0
              ? <div className="text-center py-12 text-muted-foreground"><ShieldAlert className="w-8 h-8 mx-auto mb-3 opacity-40" /><p className="text-sm">No confidential documents yet.</p></div>
              : <div className="space-y-2">{hrDocs.map(d => <DocRow key={d.id} doc={d} onDelete={handleDelete} />)}</div>
            }
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Document Title *</label>
              <Input value={uploadForm.title} onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Food Hygiene Certificate" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Type</label>
                <Select value={uploadForm.documentType} onValueChange={v => setUploadForm(f => ({ ...f, documentType: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DOC_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {isAdmin && (
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Visibility</label>
                  <Select value={uploadForm.visibility} onValueChange={v => setUploadForm(f => ({ ...f, visibility: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All Staff">All Staff</SelectItem>
                      <SelectItem value="By Department">By Department</SelectItem>
                      <SelectItem value="By Role">By Role</SelectItem>
                      <SelectItem value="Individual">Individual</SelectItem>
                      <SelectItem value="admin-only">Admin Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Expiry Date (optional)</label>
              <Input type="date" value={uploadForm.expiryDate} onChange={e => setUploadForm(f => ({ ...f, expiryDate: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">Select File *</label>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={e => setPendingFile(e.target.files?.[0] || null)} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => fileRef.current?.click()} className="flex-1 justify-start text-muted-foreground">
                  <Upload className="w-4 h-4 mr-2" />{pendingFile ? pendingFile.name : 'Choose file'}
                </Button>
              </div>
            </div>
            <Button onClick={handleUpload} disabled={uploading || !pendingFile || !uploadForm.title} className="w-full bg-primary text-primary-foreground">
              {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : 'Upload Document'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
